import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DatePicker } from '../../../components/ui/DatePicker';
import { Modal } from '../../../components/ui/Modal';
import { VoucherLines } from '../../../components/voucher/VoucherLines';
import { VoucherType, TransactionMode, VoucherFormData } from '../../../types/voucher.types';
import { voucherService } from '../../../services/voucher.service';
export function JournalCreate() {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saveAction, setSaveAction] = useState<'draft' | 'complete'>('draft');
  const [formData, setFormData] = useState<VoucherFormData>({
    voucherType: VoucherType.JOURNAL,
    date: new Date().toISOString().split('T')[0],
    transactionMode: TransactionMode.ONLINE_TRANSFER,
    lines: [{
      accountId: '',
      debit: 0,
      credit: 0,
      description: ''
    }, {
      accountId: '',
      debit: 0,
      credit: 0,
      description: ''
    }]
  });
  const mockAccounts = [{
    value: '1',
    label: '1100 - Cash'
  }, {
    value: '2',
    label: '1200 - Bank Account'
  }, {
    value: '3',
    label: '1300 - Accounts Receivable'
  }, {
    value: '4',
    label: '2000 - Accounts Payable'
  }, {
    value: '5',
    label: '3000 - Capital'
  }, {
    value: '6',
    label: '4000 - Revenue'
  }, {
    value: '7',
    label: '5000 - Expenses'
  }];
  const handleSubmit = async (action: 'draft' | 'complete') => {
    if (!voucherService.validateBalance(formData.lines)) {
      alert('Debit and Credit must be equal. Journal entries must be balanced.');
      return;
    }
    if (formData.lines.length < 2) {
      alert('Journal entry must have at least 2 lines');
      return;
    }
    setSaveAction(action);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    try {
      const voucher = await voucherService.createVoucher(formData);
      if (saveAction === 'complete') {
        await voucherService.completeVoucher(voucher.id);
      }
      navigate('/voucher/journal');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setShowConfirmModal(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1684">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-1685">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-1686">
          <Button variant="ghost" onClick={() => navigate('/voucher/journal')} data-id="element-1687">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1688" />
          </Button>
          <div data-id="element-1689">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1690">
              Create Journal Voucher
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1691">
              Record journal entries and adjustments
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={e => {
        e.preventDefault();
        handleSubmit('draft');
      }} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-1692">
          {/* Basic Information */}
          <div data-id="element-1693">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1694">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1695">
              <DatePicker label="Transaction Date" required value={formData.date} onChange={date => setFormData({
              ...formData,
              date
            })} data-id="element-1696" />
            </div>
          </div>

          {/* Important Note */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-1697">
            <p className="text-sm text-yellow-800" data-id="element-1698">
              <strong data-id="element-1699">Important:</strong> Journal entries must be balanced.
              Total Debit must equal Total Credit before you can save.
            </p>
          </div>

          {/* Transaction Lines */}
          <VoucherLines lines={formData.lines} onChange={lines => setFormData({
          ...formData,
          lines
        })} accounts={mockAccounts} data-id="element-1700" />

          {/* Narration */}
          <div data-id="element-1701">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1702">
              Narration
            </label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.narration} onChange={e => setFormData({
            ...formData,
            narration: e.target.value
          })} placeholder="Enter overall narration for this journal entry..." data-id="element-1703" />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-1704">
            <Button type="button" variant="secondary" onClick={() => navigate('/voucher/journal')} data-id="element-1705">
              Cancel
            </Button>
            <Button type="submit" variant="secondary" data-id="element-1706">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-1707" />
              Save as Draft
            </Button>
            <Button type="button" variant="primary" onClick={() => handleSubmit('complete')} data-id="element-1708">
              <CheckIcon className="w-4 h-4 mr-2" data-id="element-1709" />
              Save & Complete
            </Button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title={saveAction === 'draft' ? 'Save as Draft' : 'Save & Complete'} footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-1711">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-1712">
              Confirm
            </Button>
          </>} data-id="element-1710">
        <p className="text-gray-700" data-id="element-1713">
          {saveAction === 'draft' ? 'Save this journal entry as draft?' : 'Complete this journal entry? It will be locked for editing.'}
        </p>
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm" data-id="element-1714">
          <div data-id="element-1715">
            <strong data-id="element-1716">Type:</strong> Journal Voucher
          </div>
          <div data-id="element-1717">
            <strong data-id="element-1718">Date:</strong> {formData.date}
          </div>
          <div data-id="element-1719">
            <strong data-id="element-1720">Lines:</strong> {formData.lines.length}
          </div>
          <div data-id="element-1721">
            <strong data-id="element-1722">Total:</strong> $
            {formData.lines.reduce((sum, line) => sum + line.debit, 0).toLocaleString()}
          </div>
        </div>
      </Modal>
    </div>;
}